import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { Session } from '@/api/client';
import { getSessionReaderIds } from '@/utils/projectRecordingSessions';
import {
  compareReaderPlanned,
  isReaderPlannedSession,
  parseReaderPlannedCursor,
  type ReaderPlannedCounts,
  type ReaderPlannedOrderKey,
  type ReaderPlannedPage,
} from '@sessions/readerPlanned';

/**
 * Кэш «В других проектах» нового дизайна: порции сессий (GET /sessions/reader-planned) и числа (…/counts).
 * Сокет патчит загруженные порции точечно, порядок — общий с сервером (compareReaderPlanned). Числа патчатся, когда прежнее
 * состояние сессии известно (она в загруженных порциях или загружено всё); иначе — перечитываются только числа.
 */

export const READER_PLANNED_PAGES_ROOT = 'readerPlannedPages' as const;
export const READER_PLANNED_COUNTS_ROOT = 'readerPlannedCounts' as const;

export type ReaderPlannedPages = InfiniteData<ReaderPlannedPage<Session>, string | null>;

function readersKey(readerIds: readonly string[]): string {
  return [...new Set(readerIds.filter(Boolean))].sort().join(',');
}

export function readerPlannedPagesKey(readerIds: readonly string[], excludeProjectId: string) {
  return [READER_PLANNED_PAGES_ROOT, readersKey(readerIds), excludeProjectId] as const;
}

export function readerPlannedCountsKey(readerIds: readonly string[], excludeProjectId: string) {
  return [READER_PLANNED_COUNTS_ROOT, readersKey(readerIds), excludeProjectId] as const;
}

function parseKey(queryKey: readonly unknown[]): { readerIds: string[]; excludeProjectId: string } | null {
  const [, readers, excludeProjectId] = queryKey;
  if (typeof readers !== 'string' || typeof excludeProjectId !== 'string') return null;
  return { readerIds: readers ? readers.split(',') : [], excludeProjectId };
}

/** Начало в порядке сервера — «ЧЧ:ММ:СС» (локальная правка старого окна может прийти с «ЧЧ:ММ») */
function orderKey(s: Pick<Session, 'id' | 'date' | 'start'>): ReaderPlannedOrderKey {
  const start = s.start ?? '';
  return { date: s.date, start: start.length === 5 ? `${start}:00` : start, id: s.id };
}

/** Чтецы списка в сессии; пусто — сессия в список не входит */
function listReaders(s: Session, readerIds: readonly string[]): string[] {
  return getSessionReaderIds(s).filter((id) => readerIds.includes(id));
}

function belongs(s: Session, readerIds: readonly string[], excludeProjectId: string): boolean {
  return isReaderPlannedSession(s) && s.project_id !== excludeProjectId && listReaders(s, readerIds).length > 0;
}

function findLoaded(data: ReaderPlannedPages, id: string): Session | null {
  for (const page of data.pages) {
    const found = page.items.find((s) => s.id === id);
    if (found) return found;
  }
  return null;
}

const allLoaded = (data: ReaderPlannedPages) => data.pages.at(-1)?.nextCursor === null;

/** Дальше последней загруженной — придёт со следующей порцией; ближе — в порцию, чья граница её покрывает */
function pageIndexFor(data: ReaderPlannedPages, key: ReaderPlannedOrderKey): number {
  return data.pages.findIndex((page) => {
    const bound = page.nextCursor === null ? null : parseReaderPlannedCursor(page.nextCursor);
    return bound === null || compareReaderPlanned(key, bound) <= 0;
  });
}

/** Порции без сессии id и, если include, с ней на своём месте; ничего не поменялось — те же данные */
export function patchReaderPlannedPages(data: ReaderPlannedPages, session: Session, include: boolean): ReaderPlannedPages {
  let changed = false;
  const pages = data.pages.map((page) => {
    const items = page.items.filter((s) => s.id !== session.id);
    if (items.length === page.items.length) return page;
    changed = true;
    return { ...page, items };
  });
  if (include) {
    const key = orderKey(session);
    const index = pageIndexFor({ ...data, pages }, key);
    if (index >= 0) {
      const page = pages[index]!;
      const at = page.items.findIndex((s) => compareReaderPlanned(key, orderKey(s)) < 0);
      const items = [...page.items];
      items.splice(at < 0 ? items.length : at, 0, session);
      pages[index] = { ...page, items };
      changed = true;
    }
  }
  return changed ? { ...data, pages } : data;
}

/** Числа после замены prev на next (любая может отсутствовать); ничего не поменялось — те же числа */
export function patchReaderPlannedCounts(
  counts: ReaderPlannedCounts,
  prev: Session | null,
  next: Session | null,
  readerIds: readonly string[]
): ReaderPlannedCounts {
  const byReader = { ...counts.byReader };
  let total = counts.total;
  let changed = false;
  const apply = (s: Session | null, sign: 1 | -1) => {
    if (!s) return;
    total += sign;
    for (const id of listReaders(s, readerIds)) byReader[id] = Math.max(0, (byReader[id] ?? 0) + sign);
    changed = true;
  };
  apply(prev, -1);
  apply(next, 1);
  if (!changed) return counts;
  const same = total === counts.total && Object.keys(byReader).every((id) => byReader[id] === (counts.byReader[id] ?? 0));
  return same ? counts : { total: Math.max(0, total), byReader };
}

/**
 * Сессия из сокета (или своя правка старого окна): в порциях — на своё место или прочь, числа — точно, когда прежнее состояние известно.
 * Сессия за последней загруженной, которую не видели, — новая или сдвинутая: сколько их было, неизвестно, поэтому
 * перечитываются только числа этого списка (дешёвый COUNT), порции не трогаются.
 */
export function upsertReaderPlannedPagesCache(queryClient: QueryClient, session: Session): void {
  for (const [queryKey, data] of queryClient.getQueriesData<ReaderPlannedPages>({ queryKey: [READER_PLANNED_PAGES_ROOT] })) {
    const parsed = parseKey(queryKey);
    if (!data || !parsed) continue;
    const include = belongs(session, parsed.readerIds, parsed.excludeProjectId);
    const prev = findLoaded(data, session.id);
    const next = patchReaderPlannedPages(data, session, include);
    if (next !== data) queryClient.setQueryData<ReaderPlannedPages>(queryKey, next);
    syncCounts(queryClient, parsed, data, prev, include ? session : null);
  }
}

export function removeFromReaderPlannedPagesCache(queryClient: QueryClient, sessionId: string): void {
  for (const [queryKey, data] of queryClient.getQueriesData<ReaderPlannedPages>({ queryKey: [READER_PLANNED_PAGES_ROOT] })) {
    const parsed = parseKey(queryKey);
    if (!data || !parsed) continue;
    const prev = findLoaded(data, sessionId);
    if (prev) queryClient.setQueryData<ReaderPlannedPages>(queryKey, patchReaderPlannedPages(data, prev, false));
    syncCounts(queryClient, parsed, data, prev, null, true);
  }
}

function syncCounts(
  queryClient: QueryClient,
  { readerIds, excludeProjectId }: { readerIds: string[]; excludeProjectId: string },
  pagesBefore: ReaderPlannedPages,
  prev: Session | null,
  next: Session | null,
  removed = false
): void {
  const key = readerPlannedCountsKey(readerIds, excludeProjectId);
  const counts = queryClient.getQueryData<ReaderPlannedCounts>(key);
  if (!counts) return;
  if (prev || allLoaded(pagesBefore)) {
    const patched = patchReaderPlannedCounts(counts, prev, next, readerIds);
    if (patched !== counts) queryClient.setQueryData(key, patched);
    return;
  }
  // Не в загруженных порциях, а загружено не всё: было ли её в числах — неизвестно. Не входит и не удалена — считаем, что не было
  if (next || removed) void queryClient.invalidateQueries({ queryKey: key, exact: true });
}
